import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Settings2, Eye, Save, RotateCcw, ChevronDown, ChevronRight, Sparkles, FileText, ArrowLeft, DoorOpen, DoorClosed, Archive, RectangleHorizontal, CheckCircle2, AlertTriangle, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ICONS = {
  Projet: '1',
  Environnement: '2',
  Style: '3',
  'Matériaux': '4',
  Mobilier: '5',
  'Détails': '6'
};

/**
 * V14.43 — Compact card that surfaces, at a glance, how each menuiserie
 * sub-role is covered by the project's Matériauthèque. Mounted at the top
 * of `PromptConfigPage` so users see uncovered roles (which silently
 * inherit defaults at render time) BEFORE launching a generation.
 */
// V14.45 — Per-family taxonomy mirroring the backend _FAMILIES dict.
// Each sub-role declares an icon + placeholder for the quick-add modal.
const FAMILY_DEFS = {
  menuiserie: {
    label: 'Menuiseries',
    subroles: [
      { key: 'porte_entree',       label: "Porte d'entrée",          icon: DoorOpen,             placeholder: 'Ex: Bois noyer mat avec poignée laiton' },
      { key: 'portes_interieures', label: 'Portes intérieures',      icon: DoorClosed,           placeholder: 'Ex: MDF laqué blanc satiné' },
      { key: 'portes_placard',     label: 'Portes de placard',       icon: Archive,              placeholder: 'Ex: Coulissant miroir + bois clair' },
      { key: 'fenetres',           label: 'Fenêtres et baies',       icon: RectangleHorizontal,  placeholder: 'Ex: Aluminium thermolaqué noir mat' },
    ],
  },
  sols: {
    label: 'Sols',
    subroles: [
      { key: 'sol_interieur_souple', label: 'Sol intérieur souple', icon: RectangleHorizontal, placeholder: 'Ex: Parquet chêne huilé point de Hongrie' },
      { key: 'sol_interieur_dur',    label: 'Sol intérieur dur',    icon: RectangleHorizontal, placeholder: 'Ex: Carrelage 60x60 grès cérame effet pierre' },
      { key: 'sol_exterieur',        label: 'Sol extérieur',        icon: RectangleHorizontal, placeholder: 'Ex: Dalles pierre reconstituée beige' },
    ],
  },
  murs: {
    label: 'Murs',
    subroles: [
      { key: 'peinture_murale',   label: 'Peinture murale',       icon: RectangleHorizontal, placeholder: 'Ex: Peinture mate blanc cassé Farrow & Ball' },
      { key: 'faience_murale',    label: 'Faïence / carrelage',   icon: RectangleHorizontal, placeholder: 'Ex: Zelliges blancs 10x10 SDB' },
      { key: 'mur_accent',        label: "Mur d'accent",          icon: RectangleHorizontal, placeholder: 'Ex: Papier peint floral vert sauge' },
    ],
  },
  facades: {
    label: 'Façades',
    subroles: [
      { key: 'facade_enduit',        label: 'Enduit / ITE',              icon: RectangleHorizontal, placeholder: 'Ex: Enduit taloché ocre rosé' },
      { key: 'facade_bardage',       label: 'Bardage',                   icon: RectangleHorizontal, placeholder: 'Ex: Bardage mélèze claire-voie' },
      { key: 'facade_pierre_brique', label: 'Pierre / brique / parement', icon: RectangleHorizontal, placeholder: 'Ex: Brique rouge flammée' },
      { key: 'soubassement',         label: 'Soubassement',              icon: RectangleHorizontal, placeholder: 'Ex: Pierre de taille grise' },
      { key: 'garde_corps_ext',      label: 'Garde-corps & balcons',     icon: RectangleHorizontal, placeholder: 'Ex: Garde-corps acier thermolaqué noir' },
    ],
  },
  toitures: {
    label: 'Toitures',
    subroles: [
      { key: 'toiture_tuiles',   label: 'Tuiles',          icon: RectangleHorizontal, placeholder: 'Ex: Tuiles terre cuite rouge vieilli' },
      { key: 'toiture_ardoise',  label: 'Ardoise',         icon: RectangleHorizontal, placeholder: 'Ex: Ardoise naturelle 32×22' },
      { key: 'toiture_zinc_bac', label: 'Zinc / bac acier', icon: RectangleHorizontal, placeholder: 'Ex: Zinc quartz joint debout' },
      { key: 'toit_terrasse',    label: 'Toit-terrasse',   icon: RectangleHorizontal, placeholder: 'Ex: Étanchéité gravillonnée / végétalisée' },
    ],
  },
  exterieurs: {
    label: 'Abords & sols extérieurs',
    subroles: [
      { key: 'voirie',      label: 'Voirie & stationnement',    icon: RectangleHorizontal, placeholder: 'Ex: Enrobé noir + pavés granit' },
      { key: 'trottoir',    label: 'Trottoirs & cheminements', icon: RectangleHorizontal, placeholder: 'Ex: Béton désactivé clair' },
      { key: 'espace_vert', label: 'Espaces verts',             icon: RectangleHorizontal, placeholder: 'Ex: Pelouse + haies de charmes' },
      { key: 'cloture',     label: 'Clôtures & portails',       icon: RectangleHorizontal, placeholder: 'Ex: Grillage rigide anthracite' },
    ],
  },
  mobilier: {
    label: 'Mobilier par pièce',
    subroles: [
      { key: 'salon',              label: 'Salon',              icon: Archive, placeholder: 'Ex: Canapé velours côtelé blanc cassé + table basse marbre' },
      { key: 'salle_manger',       label: 'Salle à manger',     icon: Archive, placeholder: 'Ex: Table ovale chêne + chaises tissu beige' },
      { key: 'chambre_principale', label: 'Chambre principale', icon: Archive, placeholder: 'Ex: Lit king size tête capitonnée + commodes laquées' },
      { key: 'chambre_enfant',     label: 'Chambre enfant',     icon: Archive, placeholder: 'Ex: Lit cabane bois clair + bureau menthe' },
      { key: 'mobilier_exterieur', label: 'Mobilier extérieur', icon: Archive, placeholder: 'Ex: Salon de jardin rotin + bains de soleil teck' },
    ],
  },
};

// Backward-compat shim used by the quick-add modal — keeps the old
// SUBROLE_DEFS lookup working for the iter-77/80 tests that pin
// `Find SUBROLE_DEFS` usage.
const SUBROLE_DEFS = FAMILY_DEFS.menuiserie.subroles;

/**
 * V14.43-V14.45 — Coverage card with per-family sections. Each section
 * lists its sub-roles as compact tiles (covered = teal ✓, missing =
 * amber ⚠️ + "Ajouter ce rôle" CTA).
 */
const FamilyCoverageSection = ({ familyKey, family, coverage, onAddMissing, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen ?? true);
  const subroles = family.subroles;
  const covered = subroles.filter((sr) => (coverage?.[sr.key]?.count || 0) > 0).length;
  const total = subroles.length;
  const hasMissing = covered < total;
  return (
    <div className="rounded-lg border border-teal-100 bg-white/70" data-testid={`coverage-family-${familyKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-teal-50/50 rounded-t-lg"
        data-testid={`coverage-family-${familyKey}-toggle`}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-teal-700" /> : <ChevronRight className="w-3.5 h-3.5 text-teal-700" />}
        <span className="text-xs font-semibold text-teal-900 flex-1">{family.label}</span>
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${hasMissing ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-700'}`}>
          {covered}/{total} couverts
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2 pt-1">
          {subroles.map(({ key, label, icon: Icon }) => {
            const entry = coverage?.[key] || { count: 0, samples: [] };
            const isCovered = entry.count > 0;
            return (
              <div
                key={key}
                className={`relative rounded-lg border p-2.5 ${isCovered
                  ? 'border-teal-300 bg-white'
                  : 'border-amber-300 bg-amber-50'}`}
                title={isCovered ? entry.samples.join(' · ') : 'Aucun matériau pour ce rôle — défaut générique au rendu'}
                data-testid={`coverage-${key}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className={`w-3.5 h-3.5 ${isCovered ? 'text-teal-600' : 'text-amber-600'}`} />
                  <span className="text-[11px] font-medium text-gray-800 flex-1 leading-tight">{label}</span>
                  {isCovered ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-teal-600" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                  )}
                </div>
                <div className={`text-[10px] ${isCovered ? 'text-teal-700' : 'text-amber-700'}`}>
                  {isCovered ? `${entry.count} matériau${entry.count > 1 ? 'x' : ''}` : 'Aucun — défaut au rendu'}
                </div>
                {isCovered && entry.samples.length > 0 && (
                  <div className="text-[9.5px] text-gray-500 mt-1 truncate" title={entry.samples.join(' · ')}>
                    {entry.samples[0]}{entry.samples.length > 1 ? ` +${entry.samples.length - 1}` : ''}
                  </div>
                )}
                {!isCovered && onAddMissing && (
                  <button
                    type="button"
                    onClick={() => onAddMissing(key, label, familyKey)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 transition-colors"
                    data-testid={`coverage-${key}-add-btn`}
                  >
                    <Plus className="w-3 h-3" />
                    Ajouter ce rôle
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MateriauthequeCoverageCard = ({ coverage, onAddMissing }) => {
  // V14.45 — Prefer the new `families` payload; fall back to the V14.43
  // top-level `menuiserie` key for backward compatibility.
  const families = coverage?.families;
  const other = coverage?.other_fields || {};

  // Compute global counts for the header
  const familyKeys = Object.keys(FAMILY_DEFS);
  let totalCovered = 0;
  let totalSlots = 0;
  familyKeys.forEach((fk) => {
    const fDefs = FAMILY_DEFS[fk].subroles;
    const fCov = (families?.[fk]?.subroles) || (fk === 'menuiserie' ? coverage?.menuiserie : {}) || {};
    totalSlots += fDefs.length;
    fDefs.forEach((sr) => {
      if ((fCov?.[sr.key]?.count || 0) > 0) totalCovered += 1;
    });
  });

  return (
    <div
      className="mb-4 rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-white p-4"
      data-testid="materiautheque-coverage-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-teal-600" />
        <h3 className="text-sm font-semibold text-teal-900">Couverture Matériauthèque</h3>
        <span className="text-[11px] text-teal-700 ml-auto">
          {totalSlots === 0
            ? 'Aucun rôle déclaré'
            : `${totalCovered}/${totalSlots} rôles couverts`}
        </span>
      </div>

      <div className="space-y-2">
        {familyKeys.map((fk, i) => {
          const familyCov = families?.[fk]?.subroles
            || (fk === 'menuiserie' ? coverage?.menuiserie : {})
            || {};
          return (
            <FamilyCoverageSection
              key={fk}
              familyKey={fk}
              family={FAMILY_DEFS[fk]}
              coverage={familyCov}
              onAddMissing={onAddMissing}
              defaultOpen={i === 0}
            />
          );
        })}
      </div>

      {Object.keys(other).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="coverage-other-fields">
          <span className="text-[10px] text-gray-500 mr-1">Autres familles couvertes :</span>
          {Object.entries(other).map(([k, v]) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-teal-100 text-teal-700 border border-teal-200"
              title={(v?.samples || []).join(' · ')}
            >
              {k} · {v.count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * V14.44 — Lightweight modal to author one missing menuiserie material
 * without leaving Config Prompt. Submits to POST /api/projects/{id}/
 * materialtheque/projects/{id}/quick-add and triggers a coverage refetch
 * on success.
 */
const QuickAddMaterialModal = ({ open, subrole, label, placeholder, onClose, onSubmit, saving }) => {
  // No reset useEffect needed — the parent mounts/unmounts this component
  // each time `quickAddSubrole` toggles, so useState defaults to '' on
  // every open.
  const [name, setName] = useState('');
  const [finish, setFinish] = useState('');
  const [ref, setRef] = useState('');

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Le nom du matériau est requis');
      return;
    }
    onSubmit({ name: trimmed, finish: finish.trim() || null, ref: ref.trim() || null });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="quick-add-modal"
    >
      <div
        className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded hover:bg-gray-100 text-gray-500"
          aria-label="Fermer"
          data-testid="quick-add-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Plus className="w-4 h-4 text-teal-600" />
          <h3 className="text-sm font-semibold text-gray-900">Ajouter — {label}</h3>
        </div>
        <p className="text-[11px] text-gray-500 mb-4">
          Ce matériau sera ajouté à la Matériauthèque du projet et utilisé dans les prochains rendus.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[11px] font-medium text-gray-700 block mb-1" htmlFor="qam-name">
              Nom du matériau <span className="text-red-500">*</span>
            </label>
            <Input
              id="qam-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={placeholder}
              className="h-9 text-sm"
              autoFocus
              data-testid="quick-add-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-gray-700 block mb-1" htmlFor="qam-finish">
                Finition
              </label>
              <Input
                id="qam-finish"
                value={finish}
                onChange={(e) => setFinish(e.target.value)}
                placeholder="mat, satiné, brillant…"
                className="h-9 text-sm"
                data-testid="quick-add-finish-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-gray-700 block mb-1" htmlFor="qam-ref">
                Référence (optionnel)
              </label>
              <Input
                id="qam-ref"
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                placeholder="BAT-MEN-XX-01"
                className="h-9 text-sm"
                data-testid="quick-add-ref-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={saving}
              data-testid="quick-add-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving || !name.trim()}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="quick-add-submit-btn"
            >
              {saving ? 'Ajout…' : 'Ajouter'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PromptConfigPage = () => {
  const { user } = useAuth();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isGlobal = !projectId;
  const [schema, setSchema] = useState({ variables: [], groups: [] });
  const [values, setValues] = useState({});
  const [template, setTemplate] = useState('');
  const [preview, setPreview] = useState('');
  const [previewMeta, setPreviewMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [showTemplate, setShowTemplate] = useState(false);
  const [projectName, setProjectName] = useState('');
  // Decoration styles (predefined + user-saved + from Matériauthèque "Save as Style").
  // Surfaced in the new `style_ref` field so the prompt actually points to the
  // styles the user already created instead of being orphaned.
  const [decorationStyles, setDecorationStyles] = useState([]);

  // V14.41 — Auto-fills derived from the project's Matériauthèque. Empty
  // user fields fall back to these phrases at generation time. Surfaced in
  // the UI as placeholder text + a "🪄 auto-rempli" badge so the user knows
  // the field is covered even if it looks blank.
  const [materiauthequeFills, setMateriauthequeFills] = useState({});
  // V14.43 — Coverage report (menuiserie sub-roles + other field counts).
  const [materiauthequeCoverage, setMateriauthequeCoverage] = useState(null);
  // V14.44 — Quick-add modal state (open + targeted sub-role + saving flag).
  const [quickAddSubrole, setQuickAddSubrole] = useState(null);
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [coverageReloadTick, setCoverageReloadTick] = useState(0);

  // Reload fills+coverage on demand (after a successful quick-add).
  const refetchCoverage = useCallback(() => {
    setCoverageReloadTick((t) => t + 1);
  }, []);

  const handleAddMissing = useCallback((subroleKey, label, familyKey) => {
    // V14.45 — look up the sub-role definition across all families.
    let def = null;
    if (familyKey && FAMILY_DEFS[familyKey]) {
      def = FAMILY_DEFS[familyKey].subroles.find((d) => d.key === subroleKey);
    }
    if (!def) {
      for (const fk of Object.keys(FAMILY_DEFS)) {
        const f = FAMILY_DEFS[fk].subroles.find((d) => d.key === subroleKey);
        if (f) { def = f; break; }
      }
    }
    setQuickAddSubrole({
      key: subroleKey,
      label,
      placeholder: def?.placeholder || `Décrire le matériau (${label.toLowerCase()})`,
    });
  }, []);

  const handleQuickAddSubmit = useCallback(async ({ name, finish, ref }) => {
    if (!projectId || !quickAddSubrole) return;
    setQuickAddSaving(true);
    try {
      await axios.post(
        `${API}/materialtheque/projects/${projectId}/quick-add`,
        { subrole: quickAddSubrole.key, name, finish, ref },
        { withCredentials: true },
      );
      toast.success(`Matériau ajouté pour : ${quickAddSubrole.label}`);
      setQuickAddSubrole(null);
      refetchCoverage();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Échec de l\'ajout — réessayez';
      toast.error(msg);
    } finally {
      setQuickAddSaving(false);
    }
  }, [projectId, quickAddSubrole, refetchCoverage]);

  useEffect(() => {
    // Skip when on the global page or before the project id resolves.
    // No top-level setState : the coverage card is gated by
    // `!isGlobal && materiauthequeCoverage` in the JSX anyway.
    if (isGlobal || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/projects/${projectId}/prompt-config/materiautheque-fills`,
          { withCredentials: true },
        );
        if (!cancelled) {
          setMateriauthequeFills(r.data?.fills || {});
          setMateriauthequeCoverage(r.data?.coverage || null);
        }
      } catch {
        if (!cancelled) {
          setMateriauthequeFills({});
          setMateriauthequeCoverage(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isGlobal, projectId, coverageReloadTick]);

  const fetchConfig = useCallback(async () => {
    try {
      const [schemaRes, configRes] = await Promise.all([
        axios.get(`${API}/prompt-config/schema`, { withCredentials: true }),
        isGlobal
          ? axios.get(`${API}/prompt-config/default`, { withCredentials: true })
          : axios.get(`${API}/projects/${projectId}/prompt-config`, { withCredentials: true })
      ]);
      setSchema(schemaRes.data);
      setValues(configRes.data.values || {});
      setTemplate(configRes.data.template || '');

      if (!isGlobal) {
        const projRes = await axios.get(`${API}/projects/${projectId}`, { withCredentials: true });
        setProjectName(projRes.data.name);
      }
    } catch (error) {
      toast.error('Erreur chargement configuration');
    } finally {
      setLoading(false);
    }
  }, [isGlobal, projectId]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  // Load decoration styles once — used by the `style_ref` field.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/styles`, { withCredentials: true });
        if (!cancelled) setDecorationStyles(r.data || []);
      } catch { /* leave empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = isGlobal
        ? `${API}/prompt-config/default`
        : `${API}/projects/${projectId}/prompt-config`;
      await axios.put(endpoint, { values, template }, { withCredentials: true });
      toast.success('Configuration sauvegardée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await axios.post(`${API}/prompt-config/preview`,
        { values, template, project_id: projectId || undefined },
        { withCredentials: true });
      setPreview(res.data.preview);
      setPreviewMeta({
        prompt_mode: res.data.prompt_mode,
        lock_source: res.data.lock_source,
        editable: res.data.editable !== false,
        notice: res.data.notice || null,
        materials_brief_present: !!res.data.materials_brief_present,
      });
      setShowPreview(true);
    } catch { toast.error('Erreur preview'); }
  };

  const handleReset = async () => {
    if (!window.confirm('Remettre les valeurs par défaut ?')) return;
    try {
      const res = await axios.get(`${API}/prompt-config/default`, { withCredentials: true });
      setValues(res.data.values || {});
      setTemplate(res.data.template || '');
      toast.success('Valeurs par défaut restaurées');
    } catch { toast.error('Erreur reset'); }
  };

  const toggleGroup = (group) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const updateValue = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-hud-page data-testid="prompt-config-page" className="fade-in max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button
            type="button"
            onClick={() => projectId ? navigate(`/projects/${projectId}?section=production`) : navigate('/projects')}
            data-testid="prompt-config-back-btn"
            className="hud-btn hud-btn-ghost hud-btn-sm mb-3"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {projectId ? 'Retour au Studio' : 'Retour aux projets'}
          </button>
          <h1 className="text-2xl font-semibold text-gray-950 font-['Outfit'] flex items-center gap-2">
            <Settings2 className="w-6 h-6 text-blue-600" />
            Configuration du Prompt
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {isGlobal ? 'Configuration globale par défaut (Admin)' : `Projet : ${projectName}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-config-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Défaut
          </Button>
          <Button variant="outline" size="sm" onClick={handlePreview} data-testid="preview-prompt-btn">
            <Eye className="w-3.5 h-3.5 mr-1.5" />Aperçu
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-config-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" />{saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      {/* V14.43 — Couverture Matériauthèque par sous-rôle de menuiserie */}
      {!isGlobal && materiauthequeCoverage && (
        <MateriauthequeCoverageCard
          coverage={materiauthequeCoverage}
          onAddMissing={handleAddMissing}
        />
      )}

      {/* V14.44 — Quick-add modal */}
      {quickAddSubrole && (
        <QuickAddMaterialModal
          open={!!quickAddSubrole}
          subrole={quickAddSubrole.key}
          label={quickAddSubrole.label}
          placeholder={quickAddSubrole.placeholder}
          onClose={() => !quickAddSaving && setQuickAddSubrole(null)}
          onSubmit={handleQuickAddSubmit}
          saving={quickAddSaving}
        />
      )}

      {/* Variable groups */}
      <div className="space-y-4 mb-6">
        {schema.groups.map((group, gi) => (
          <div key={group} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`group-${group}`}>
            <button
              className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
              onClick={() => toggleGroup(group)}
            >
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                {GROUP_ICONS[group] || gi + 1}
              </span>
              <span className="text-sm font-semibold text-gray-900 flex-1">{group}</span>
              <span className="text-xs text-gray-400">{schema.variables.filter(v => v.group === group).length} paramètres</span>
              {collapsedGroups[group]
                ? <ChevronRight className="w-4 h-4 text-gray-400" />
                : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {!collapsedGroups[group] && (
              <div className="px-5 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-gray-100 pt-3">
                {schema.variables.filter(v => v.group === group).map(variable => {
                  // V14.41 — Matériauthèque auto-fill hint for empty fields.
                  const autoFill = materiauthequeFills[variable.key];
                  const userVal = values[variable.key];
                  const isAutoFilled = !!autoFill && !(userVal && String(userVal).trim());
                  return (
                  <div key={variable.key} data-testid={`var-${variable.key}`}>
                    <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1.5 flex-wrap">
                      <span>{variable.label}</span>
                      {isAutoFilled && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9.5px] font-semibold"
                          style={{
                            background: 'rgba(94, 234, 212, 0.12)',
                            color: '#0D9488',
                            border: '1px solid rgba(94, 234, 212, 0.4)',
                          }}
                          title={`Auto-rempli depuis la Matériauthèque : ${autoFill}`}
                          data-testid={`var-${variable.key}-autofill-badge`}
                        >
                          🪄 auto-rempli
                        </span>
                      )}
                    </label>
                    {variable.type === 'select' ? (
                      <Select
                        value={values[variable.key] || variable.options?.[0] || ''}
                        onValueChange={(v) => updateValue(variable.key, v)}
                      >
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {variable.options?.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : variable.type === 'style_ref' ? (
                      <Select
                        value={values[variable.key] || 'none'}
                        onValueChange={(v) => updateValue(variable.key, v === 'none' ? '' : v)}
                      >
                        <SelectTrigger className="h-9 text-sm" data-testid={`var-${variable.key}-select`}>
                          <SelectValue placeholder={variable.placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Aucun (utiliser CCTP / texte libre) —</SelectItem>
                          {decorationStyles.map((s) => (
                            <SelectItem key={s.style_id} value={s.style_id}>
                              {s.name}
                              {s.materials_count ? ` · ${s.materials_count} matériaux` : ''}
                              {s.is_predefined ? ' · pré-défini' : ''}
                            </SelectItem>
                          ))}
                          {decorationStyles.length === 0 && (
                            <SelectItem value="__empty__" disabled>
                              Aucun style — sauvegardez d&apos;abord depuis la Matériauthèque
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    ) : variable.type === 'number' ? (
                      <Input
                        type="number"
                        min={variable.min} max={variable.max}
                        value={values[variable.key] ?? ''}
                        onChange={(e) => updateValue(variable.key, Number(e.target.value))}
                        className="h-9 text-sm"
                      />
                    ) : (
                      <Input
                        value={values[variable.key] || ''}
                        onChange={(e) => updateValue(variable.key, e.target.value)}
                        placeholder={autoFill || variable.placeholder}
                        className={`h-9 text-sm ${isAutoFilled ? 'placeholder:text-teal-600 placeholder:italic' : ''}`}
                      />
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Prompt template editor */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <button
          className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
          onClick={() => setShowTemplate(!showTemplate)}
        >
          <FileText className="w-5 h-5 text-indigo-500" />
          <span className="text-sm font-semibold text-gray-900 flex-1">Template du Prompt (Avancé)</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200"
                title="Ce template libre n'est PAS utilisé lors des générations : le prompt réel est construit automatiquement depuis vos valeurs, la Matériauthèque et la géométrie tracée. Utilisez « Aperçu » pour voir le prompt exact."
                data-testid="prompt-template-obsolete-badge">
            ⚠ non utilisé à la génération
          </span>
          <span className="text-xs text-gray-400">Utilisez {'{variable}'} pour injecter les valeurs</span>
          {showTemplate ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </button>
        {showTemplate && (
          <div className="px-5 pb-4 border-t border-gray-100 pt-3">
            <Textarea
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={18}
              className="font-mono text-xs leading-relaxed"
              data-testid="prompt-template-editor"
            />
            <p className="text-[10px] text-gray-400 mt-2">
              Variables disponibles : {schema.variables.map(v => `{${v.key}}`).join(', ')}
            </p>
          </div>
        )}
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />Aperçu du Prompt Généré
              </h3>
              <Button variant="outline" size="sm" onClick={() => setShowPreview(false)}>Fermer</Button>
            </div>
            {/* Sprint 29 — Dual-prompting mode badge */}
            <div
              data-testid="prompt-mode-badge"
              className={`px-5 py-3 border-b text-xs font-medium flex items-start gap-2 ${
                previewMeta?.lock_source
                  ? 'bg-emerald-50 border-emerald-100 text-emerald-800'
                  : 'bg-sky-50 border-sky-100 text-sky-800'
              }`}
            >
              <span className="text-base leading-none mt-0.5">
                {previewMeta?.lock_source ? '🔒' : '✏️'}
              </span>
              <div className="flex-1">
                <div className="font-semibold">
                  {previewMeta?.lock_source
                    ? `Mode : ControlNet verrouillé (${previewMeta.lock_source})`
                    : 'Mode : Prompt descriptif legacy (modifiable)'}
                </div>
                <div className="opacity-80 mt-0.5">
                  {previewMeta?.notice || (
                    previewMeta?.lock_source
                      ? 'La géométrie est figée par le binding — ce prompt allégé pilote uniquement le texturing/staging/lumière.'
                      : 'Aperçu = prompt réel envoyé à la génération (valeurs + Matériauthèque + typologie).'
                  )}
                  {previewMeta?.materials_brief_present && (
                    <span className="ml-1 font-semibold" data-testid="prompt-preview-materiautheque-badge">· Matériauthèque incluse ✓</span>
                  )}
                </div>
              </div>
            </div>
            <div className="p-5 overflow-y-auto max-h-[65vh]">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed bg-gray-50 p-4 rounded-lg" data-testid="prompt-preview-text">
                {preview}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptConfigPage;
